import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  BanknotesIcon,
  CalendarIcon,
  CursorArrowRaysIcon,
  PlayIcon,
  PauseIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useCampaigns } from '../hooks/useCampaigns';
import CampaignFilters from '../components/campaigns/CampaignFilters';
import CampaignForm from '../components/campaigns/CampaignForm';
import type { Campaign, CampaignFormData, CampaignStatus } from '../types/campaign';

const Campaigns: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  
  const {
    campaigns,
    loading,
    error,
    pagination,
    filters,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    toggleCampaignStatus,
    setFilters,
    setPage,
    refreshCampaigns
  } = useCampaigns();

  const handleCreateCampaign = async (data: CampaignFormData) => {
    try {
      await createCampaign(data);
      setShowForm(false);
    } catch (error) {
      console.error('Failed to create campaign:', error);
    }
  };

  const handleUpdateCampaign = async (data: CampaignFormData) => {
    if (!editingCampaign) return;
    
    try {
      await updateCampaign(editingCampaign.id, data);
      setEditingCampaign(null);
      setShowForm(false);
    } catch (error) {
      console.error('Failed to update campaign:', error);
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteCampaign(id);
    } catch (error) {
      console.error('Failed to delete campaign:', error);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await toggleCampaignStatus(id);
    } catch (error) {
      console.error('Failed to toggle campaign status:', error);
    }
  };

  // Convert Campaign to CampaignFormData for editing
  const convertCampaignToFormData = (campaign: Campaign): Partial<CampaignFormData> => {
    return {
      ...campaign,
      targetKeywords: campaign.targetKeywords?.map(keyword => keyword.keyword) || []
    };
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCampaign(null);
  };

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      includeMetrics: true
    });
  };

  const getStatusColor = (status: CampaignStatus): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: CampaignStatus): string => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'paused':
        return 'Duraklatıldı';
      case 'completed':
        return 'Tamamlandı';
      case 'draft':
        return 'Taslak';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: CampaignStatus) => {
    switch (status) {
      case 'active':
        return <PlayIcon className="h-4 w-4 text-green-600" />;
      case 'paused':
        return <PauseIcon className="h-4 w-4 text-yellow-600" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-blue-600" />;
      case 'draft':
        return <ExclamationTriangleIcon className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  if (showForm) {
    return (
      <CampaignForm
        initialData={editingCampaign ? convertCampaignToFormData(editingCampaign) : undefined}
        onSubmit={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
        onCancel={handleCancelForm}
        loading={loading}
        mode={editingCampaign ? 'edit' : 'create'}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">Kampanya Yönetimi</h1>
          <p className="mt-1 text-sm text-gray-500">
            Reklam kampanyalarınızı oluşturun ve yönetin
          </p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Yeni Kampanya
        </button>
      </div>

      {/* Filters */}
      <CampaignFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Hata</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={refreshCampaigns}
                className="mt-2 text-sm text-red-600 hover:text-red-500"
              >
                Tekrar dene
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && campaigns.length === 0 && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Kampanyalar yükleniyor...</p>
        </div>
      )}

      {/* Campaigns List */}
      {!loading || campaigns.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {campaigns.map((campaign) => (
              <li key={campaign.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {getStatusIcon(campaign.status || 'draft')}
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <Link
                            to={`/campaigns/${campaign.id}`}
                            className="text-lg font-medium text-gray-900 hover:text-blue-600 truncate"
                          >
                            {campaign.name}
                          </Link>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status || 'draft')}`}>
                            {getStatusText(campaign.status || 'draft')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{campaign.description}</p>
                        <p className="text-sm text-gray-500">
                          <span className="font-medium">Tip:</span> {campaign.campaignType}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/campaigns/${campaign.id}`}
                        className="p-1.5 text-gray-400 hover:text-blue-600"
                        title="Detaylı görüntüle"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                      <button 
                        onClick={() => handleToggleStatus(campaign.id)}
                        className="p-1.5 text-gray-400 hover:text-blue-600"
                        title={campaign.status === 'active' ? 'Duraklat' : 'Başlat'}
                      >
                        {campaign.status === 'active' ? 
                          <PauseIcon className="h-4 w-4" /> : 
                          <PlayIcon className="h-4 w-4" />
                        }
                      </button>
                      <Link
                        to={`/campaigns/${campaign.id}/edit`}
                        className="p-1.5 text-gray-400 hover:text-gray-600"
                        title="Düzenle"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Link>
                      <button 
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600"
                        title="Sil"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Campaign Stats */}
                  <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <BanknotesIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">Bütçe</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(campaign.spent || 0)} / {formatCurrency(campaign.budget || 0)}
                      </p>
                      <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full" 
                          style={{ width: `${Math.min(((campaign.spent || 0) / (campaign.budget || 1)) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <EyeIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">Gösterim</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {(campaign.impressions || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <CursorArrowRaysIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">Tıklama</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {(campaign.clicks || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <span className="text-xs text-gray-500">CTR</span>
                      <p className="text-sm font-medium text-gray-900">
                        {(campaign.ctr || 0).toFixed(2)}%
                      </p>
                    </div>
                    <div className="text-center">
                      <span className="text-xs text-gray-500">CPM</span>
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(campaign.cpm || 0)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">Süre</span>
                      </div>
                      <p className="text-xs text-gray-900">
                        {campaign.startDate ? formatDate(campaign.startDate) : '-'} - {campaign.endDate ? formatDate(campaign.endDate) : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Keywords */}
                  {campaign.targetKeywords && campaign.targetKeywords.length > 0 && (
                    <div className="mt-3">
                      <span className="text-xs text-gray-500">Hedef Kelimeler:</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {campaign.targetKeywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {keyword.keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Önceki
            </button>
            <button
              onClick={() => setPage(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                {' - '}
                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                {' / '}
                <span className="font-medium">{pagination.total}</span>
                {' sonuç gösteriliyor'}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Önceki
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sonraki
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && campaigns.length === 0 && (
        <div className="text-center py-12">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Kampanya bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500">
            Arama kriterlerinize uygun kampanya bulunamadı.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              İlk kampanyanızı oluşturun
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;